/**
 * cmfGuideData.ts — Circular CMF Guide data registry (CX-2725)
 *
 * DATA CONTRACT:
 *   The Circular CMF Guide is spec'd as CMS-driven (Sitecore). Until a
 *   `/v0/content/circular-cmf-guide` content type exists, the page renders
 *   from this static registry. To switch to CMS data, replace `cmfGuideData`
 *   with a fetching hook that returns the same `CmfGuideEntry` shape — the
 *   landing page and chapter sub-pages consume the shape, not the source.
 *
 * TO ADD / EDIT A CHAPTER:
 *   1. Add images to `public/imgs/circular-cmf-guide/`
 *   2. Add or edit an entry in `chapters` with the chapter's slug as key
 *   3. Routing, landing cards and sub-pages pick it up automatically
 */

// ── Types ─────────────────────────────────────────────────────────────────────

/** A single chapter card on the landing page + the entry point to its sub-page. */
export interface CmfChapterCard {
  /** URL slug — matches the chapter sub-page route (CX-2726..2729). */
  slug: string;
  title: string;
  /** Short subtitle shown in the card hover-reveal. */
  subtitle: string;
  image: string;
  description: string;
  /** Tag pills shown in the card hover-reveal. */
  tags: string[];
  /** Jira ticket for the chapter sub-page — for traceability only. */
  ticket: string;
}

/** The "Circular CMF — An Overview" text block. */
export interface CmfOverview {
  title: string;
  paragraphs: string[];
}

/** Top-level landing content for the Circular CMF Guide. */
export interface CmfGuideEntry {
  /** Landing route slug. */
  slug: string;
  hero: {
    title: string;
    /** Hero chip label (eyebrow above the title). */
    subtitle: string;
    intro: string;
    /** Explore-guide CTA label. */
    ctaLabel: string;
    /** Hero background image. */
    image: string;
  };
  overview: CmfOverview;
  chapters: CmfChapterCard[];
}

// ── Dataset ─────────────────────────────────────────────────────────────────────

const DEFAULT_CHAPTER_IMAGE =
  "https://mediahub.covestro.com/transform/70b78e10-0f84-4131-afff-8ea5936e6aba/90-PCR-Post-Consumer-Recycled-Polycarbonate";

/**
 * Landing page content. Sub-page (chapter) body content is defined separately
 * per chapter in CX-2726..2729 and keyed by the same `slug` values below.
 */
export const cmfGuideData: CmfGuideEntry = {
  slug: "circular-cmf-guide",
  hero: {
    title: "Designing with Recycled Plastics",
    subtitle: "Sustainability Guide",
    intro:
      "Recycled plastics open up new possibilities — but they also come with a different set of rules. Color, finish and material selection all behave differently when recycled content enters the picture, and the choices you make as a designer directly affect whether a product can be recycled again at end of life.",
    ctaLabel: "EXPLORE GUIDE",
    image:
      "https://mediahub.covestro.com/transform/70b78e10-0f84-4131-afff-8ea5936e6aba/90-PCR-Post-Consumer-Recycled-Polycarbonate",
  },
  overview: {
    title: "Circular CMF — An Overview",
    paragraphs: [
      "Recycled plastics open up new possibilities — but they also come with a different set of rules. Color, finish and material selection all behave differently when recycled content enters the picture, and the choices you make as a designer directly affect whether a product can be recycled again at end of life.",
      "This guide walks you through what to expect, what's possible, and how to get the most out of Covestro's circular materials portfolio.",
    ],
  },
  chapters: [
    {
      slug: "reborn",
      title: "Reborn",
      subtitle: "Recycled Content, Real Trade-offs",
      image:
        "https://mediahub.covestro.com/transform/f6c7356f-0567-4d9d-96dd-23a9ce8f9f48/90-PCR-Post-Consumer-Recycled-Polycarbonate",
      description:
        "How post-consumer and post-industrial plastics are given a second life as design-grade materials.",
      tags: ["PCR", "Mechanical Recycling", "Color Constraints", "CQ"],
      ticket: "CX-2726",
    },
    {
      slug: "renewed-restored",
      title: "Renewed & Restored",
      subtitle: "Full Performance, Certified Origin",
      image:
        "https://mediahub.covestro.com/transform/90513457-0314-45b9-ba57-c3f5484651ae/Chemical-recycling",
      description:
        "Reading the grade name and restoring performance in recycled feedstock without compromising on finish.",
      tags: [
        "Bio-Based",
        "Chemical Recycling",
        "Drop-In",
        "ISCC Plus",
        "Mass Balance",
      ],
      ticket: "CX-2727",
    },
    {
      slug: "designed-to-last",
      title: "Designed to Last",
      subtitle: "CMF Decisions That Close the Loop",
      image:
        "https://mediahub.covestro.com/transform/29627d87-e665-40bf-8130-37c7c0311553/Headlamp",
      description:
        "Durability as a circular strategy — surfaces engineered to stay in use longer before they return to the loop.",
      tags: ["Design for Recycling", "Monomaterial", "Finish", "Disassembly"],
      ticket: "CX-2728",
    },
    {
      slug: "digital-twins",
      title: "Digital Twins",
      subtitle: "Dematerializing the Prototyping Phase",
      image:
        "https://mediahub.covestro.com/transform/15ea0eba-f2e2-4d39-befa-c3c8f8cedbc8/IMAGIO-from-Sketch-to-Virtual-Prototype",
      description:
        "Specifying recycled materials with confidence using accurate digital samples and rendering.",
      tags: [
        "Digital Sampling",
        "Imagio CQ",
        "Dematerialization",
        "AxF",
        "VRED",
      ],
      ticket: "CX-2729",
    },
  ],
};

/** Fallback image used when a chapter/hero image fails to load (CX-2725 AC). */
export const CMF_GUIDE_DEFAULT_IMAGE = DEFAULT_CHAPTER_IMAGE;
